import React from "react";
import styled from "styled-components";
import { colors } from "src/theme/colors";
import { ScreenReaderOnly } from "src/components/Form/styles/form";
import { line } from "src/theme/ui";
import {
  lowercaseRegex,
  numberRegex,
  specialCharRegex,
  uppercaseRegex,
} from "src/utils/regex";

/**
 * PasswordStrength — football's meter and checklist, ported.
 *
 * The original is `components/ui/PasswordStrength.tsx` in the admin portal:
 * four segments of a bar, the level named beside them, and a live checklist of
 * the requirements — visible from an EMPTY field, so a reader knows what is
 * being asked before they start typing.
 *
 * What it replaces here was five 12px/300 spans in a wrapping row at
 * `flex-basis: 33%`, held at `opacity: 0` until the field had a value, and
 * painted in the ERROR colour while unmet. So the requirements were invisible
 * exactly when they were useful — before the first keystroke — and the moment
 * you typed one character the field answered with four red lines, which is a
 * screen telling you off for being halfway through. A requirement not yet met
 * is not a mistake: it is muted until it is satisfied, and then it is green.
 *
 * The rules mirror `utils/regex`, which is what the form validates against and
 * what the backend enforces, so the checklist cannot promise something the
 * submit will refuse.
 */

type Rule = { key: string; label: string; test: (value: string) => boolean };

const RULES: Rule[] = [
  { key: "length", label: "8 characters minimum", test: (v) => v.length >= 8 },
  {
    key: "upper",
    label: "1 uppercase character",
    test: (v) => new RegExp(uppercaseRegex).test(v),
  },
  {
    key: "lower",
    label: "1 lowercase character",
    test: (v) => new RegExp(lowercaseRegex).test(v),
  },
  {
    key: "number",
    label: "1 number",
    test: (v) => new RegExp(numberRegex).test(v),
  },
  {
    key: "symbol",
    label: "1 special character",
    test: (v) => new RegExp(specialCharRegex).test(v),
  },
];

/** True once every rule is satisfied — the same bar the form submits against. */
export const isStrongPassword = (value: string) =>
  RULES.every((rule) => rule.test(value));

/**
 * Four steps, and each one is a different KIND of answer rather than four
 * shades of the same: two rules met is weak, then it climbs through the
 * warning amber and the brand's own blue to the success green. Red only at
 * the bottom, where it is true.
 */
const LEVELS = [
  { label: "Weak", fill: colors.error[500] },
  { label: "Fair", fill: colors.warning[500] },
  { label: "Good", fill: colors.primary[400] },
  { label: "Strong", fill: colors.success[500] },
];

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const Meter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Track = styled.div`
  display: flex;
  flex: 1;
  gap: 4px;
`;

/* A bar's corner is about half its height and never a pill, and an unfilled
   segment is the rule the product draws structure in. */
const Segment = styled.span<{ $fill?: string }>`
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: ${({ $fill }) => $fill ?? colors.gray[200]};
  transition: background 0.18s ease;
`;

/* A fixed width, so naming the level cannot push the bars around as it
   changes from "Weak" to "Strong". */
const Level = styled.span`
  width: 46px;
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1.3;
  text-align: right;
  color: ${colors.gray[500]};
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Item = styled.li<{ $met?: boolean }>`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11.5px;
  line-height: 1.35;
  color: ${({ $met }) => ($met ? colors.success[700] : colors.gray[500])};
  transition: color 0.18s ease;
`;

/* Met is a filled disc carrying a tick; unmet is an empty dashed ring. Two
   different marks, so the state survives greyscale and a colour deficiency. */
const Mark = styled.span<{ $met?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ $met }) => ($met ? colors.success[500] : "transparent")};
  box-shadow: ${({ $met }) =>
    $met ? "none" : `inset 0 0 0 1px ${line.dashed}`};
  color: ${colors.white};
  transition: background 0.18s ease, box-shadow 0.18s ease;
`;

const Tick = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 13l4 4L19 7"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export type PasswordStrengthProps = {
  value?: string;
  /** Announced to a screen reader; the meter itself is decorative. */
  label?: string;
  /** So the field it belongs to can point at it with aria-describedby. */
  id?: string;
};

const PasswordStrength: React.FC<PasswordStrengthProps> = ({
  value = "",
  label = "Password strength",
  id,
}) => {
  const checks = RULES.map((rule) => ({
    key: rule.key,
    label: rule.label,
    met: rule.test(value),
  }));
  const met = checks.filter((check) => check.met).length;
  const empty = value.length === 0;
  /* Two rules met is still weak; after that each rule is a step. */
  const level = met <= 2 ? 0 : met - 2;
  const { label: levelLabel, fill } = LEVELS[level];

  return (
    <Wrap id={id}>
      <Meter>
        <Track
          role="meter"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={4}
          aria-valuenow={empty ? 0 : level + 1}
          aria-valuetext={empty ? "Empty" : levelLabel}
        >
          {[0, 1, 2, 3].map((segment) => (
            <Segment
              key={segment}
              $fill={!empty && segment <= level ? fill : undefined}
            />
          ))}
        </Track>
        <Level>{empty ? "" : levelLabel}</Level>
      </Meter>

      <List>
        {checks.map((check) => (
          <Item key={check.key} $met={check.met}>
            <Mark $met={check.met}>{check.met && <Tick />}</Mark>
            {check.label}
            {/* The tick is a picture; this is the same fact in words. */}
            {check.met && <ScreenReaderOnly>, met</ScreenReaderOnly>}
          </Item>
        ))}
      </List>
    </Wrap>
  );
};

export default PasswordStrength;
